import { useLocation } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { For, Show } from "solid-js";
import type { PageSortKey } from "../../enums/scan";
import { FLOWS_VIEW, SCAN_SECTION_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { createWebsiteStore } from "../website/store";
import { FlowEvidenceNotice } from "./FlowEvidenceNotice";
import { FlowsEstatePanel } from "./FlowsEstatePanel";
import { FlowsModeBar } from "./FlowsModeBar";
import { JourneyExplorer } from "./JourneyExplorer";
import { JourneySupportingSections } from "./JourneySupportingSections";
import { PageMapLensToggle } from "./PageMapLensToggle";
import { PageQuickLookPanel } from "./PageQuickLookPanel";
import { PagesInventoryPane } from "./PagesInventoryPane";
import { PAGE_SORT_OPTIONS } from "./pageSort";
import { ScanRouteHeader } from "./ScanRouteHeader";
import { ScanShell } from "./ScanShell";
import { estateStats } from "./scanModel";
import { useFlowPaths } from "./useFlowPaths";
import { type ScoreFilter, usePagesView } from "./usePagesView";

export function ScanPages(props: { websiteId: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.PAGES}>
      <Show
        when={context.data()}
        keyed
        fallback={
          <div class="loading signal-loading">Loading scan pages...</div>
        }
      >
        {(data) => <ScanPagesView data={data} websiteId={props.websiteId} />}
      </Show>
    </ScanShell>
  );
}

function ScanPagesView(props: { data: OverviewResponse; websiteId: string }) {
  const location = useLocation();
  const {
    query,
    setQuery,
    scoreFilter,
    setScoreFilter,
    cluster,
    setCluster,
    highValueOnly,
    setHighValueOnly,
    lens,
    setLens,
    sortKey,
    setSortKey,
    selectedPageId,
    clusters,
    activeScore,
    visiblePages,
    selectedPage,
    selectedPageHidden,
    selectedCluster,
    selectPage,
    clearSelection,
    revealSelectedPage,
  } = usePagesView(() => props.data);
  const flows = useFlowPaths(() => props.data, selectedPageId);

  return (
    <div class="signal-page">
      <ScanRouteHeader
        websiteId={props.websiteId}
        section="Flows"
        title="Content & Messaging Flows"
        subtitle="Explore the current content structure, recorded relationships and plausible Current Paths through the estate."
        actions={<PageMapLensToggle lens={lens()} setLens={setLens} />}
      />

      <FlowsModeBar
        websiteId={props.websiteId}
        view={flows.view()}
        onChange={flows.setView}
        returnQuery={location.search}
      />

      <Show when={flows.view() === FLOWS_VIEW.PATHS}>
        <FlowEvidenceNotice />
        <JourneyExplorer
          data={props.data}
          flows={flows.priorityFlows()}
          selectedFlow={flows.selectedFlow()}
          selectedPath={flows.selectedPath()}
          selectedPageId={selectedPageId() ?? null}
          emptyReason={flows.preview().emptyReason}
          onSelectFlow={flows.selectFlow}
          onSelectPath={flows.selectPath}
          onSelectPage={selectPage}
        />
      </Show>

      <FlowsEstatePanel
        data={props.data}
        websiteId={props.websiteId}
        view={flows.view()}
        lens={lens()}
        selectedPage={selectedPage()}
        selectedCluster={selectedCluster()}
        selectedPath={flows.selectedPath()}
        selectedFlow={flows.selectedFlow()}
        returnQuery={location.search}
        onSelectPage={selectPage}
        onClearSelection={clearSelection}
      />

      <Show when={flows.view() === FLOWS_VIEW.PATHS}>
        <JourneySupportingSections
          data={props.data}
          websiteId={props.websiteId}
          flow={flows.selectedFlow()}
          journey={flows.selectedPath()}
          returnQuery={location.search}
        />
      </Show>

      <section
        class="signal-pages-mobile-guide"
        aria-label="Page investigation"
      >
        <strong>Investigate pages from the list</strong>
        <p>
          Select a page to see its score, cluster and recorded structural links.
          The full page map is available on larger screens.
        </p>
      </section>

      <div class="signal-filter-bar">
        <input
          value={query()}
          onInput={(event) => setQuery(event.currentTarget.value)}
          placeholder="Search page, path or issue"
          aria-label="Search pages"
        />
        <select
          value={scoreFilter()}
          onChange={(event) =>
            setScoreFilter(event.currentTarget.value as ScoreFilter)
          }
          aria-label="Filter pages by score state"
        >
          <option value="all">All statuses</option>
          <option value="poor">Needs attention</option>
          <option value="watch">Watch pages</option>
          <option value="good">Good pages</option>
        </select>
        <select
          value={cluster()}
          onChange={(event) => setCluster(event.currentTarget.value)}
          aria-label="Filter pages by cluster"
        >
          <option value="all">All clusters</option>
          <For each={clusters()}>
            {(item) => (
              <option value={item.key}>
                {item.label} ({item.pages.length})
              </option>
            )}
          </For>
        </select>
        <button
          type="button"
          class={`signal-filter-chip ${highValueOnly() ? "is-on" : ""}`}
          onClick={() => setHighValueOnly(!highValueOnly())}
        >
          High-value pages only
        </button>
        <select
          value={sortKey()}
          onChange={(event) =>
            setSortKey(event.currentTarget.value as PageSortKey)
          }
          aria-label="Sort pages"
        >
          <For each={PAGE_SORT_OPTIONS}>
            {(option) => (
              <option value={option.value}>Sort: {option.label}</option>
            )}
          </For>
        </select>
      </div>

      <section class="signal-combined-investigation">
        <PagesInventoryPane
          data={props.data}
          visiblePages={visiblePages}
          selectedPage={selectedPage}
          selectedPageHidden={selectedPageHidden}
          activeScore={activeScore}
          onSelect={selectPage}
          onReveal={revealSelectedPage}
        />

        <Show
          when={selectedPage()}
          keyed
          fallback={
            <aside class="signal-selected-panel signal-pages-orientation">
              <span class="signal-mini-kicker">Investigation context</span>
              <h2>Page context map and inventory</h2>
              <p>
                Select a page card or inventory row to inspect its score,
                connected pages, linked issues and available evidence.
              </p>
              <dl>
                <div>
                  <dt>Pages</dt>
                  <dd>{props.data.pages.length}</dd>
                </div>
                <div>
                  <dt>Clusters</dt>
                  <dd>{estateStats(props.data).clusterCount}</dd>
                </div>
                <div>
                  <dt>Structural links</dt>
                  <dd>{props.data.links.length}</dd>
                </div>
              </dl>
              <p class="signal-muted">
                Search and filters refine the inventory. The page context map
                keeps the full estate visible until you choose Reveal in list.
              </p>
            </aside>
          }
        >
          {(page) => (
            <PageQuickLookPanel
              data={props.data}
              websiteId={props.websiteId}
              page={page}
              lens={lens()}
            />
          )}
        </Show>
      </section>
    </div>
  );
}
